import { Button, Checkbox, Form, FormInstance, Input, InputNumber, Radio, RadioChangeEvent, Select, Space, Tooltip } from "antd";
import { PlusOutlined, PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import React, { ChangeEvent, Component } from "react";
import { Core } from "../infrastructure/core";
import { TrueFilter, AndFilter, ComparisonFilter, enmConditionalOperators, IFilter, IsNullFilter, NotFilter, OrFilter, FalseFilter } from "../infrastructure/filtering";
import { IBaseComponent } from "../infrastructure/baseComponent";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { DatePicker as DatePickerJalali } from "antd-jalali";
import { Lookup } from "../lookup";
import { PriceInput } from "../priceInput";
import dayjs from 'dayjs';
import { enmControlType, enmPropertyType } from "../entityManager/interfaces";
import { IReportDefaultSearchParam } from "../../report/damageCaseReport/interfaces";

const { Option } = Select;

const operators: { [key: number]: { title: string; types: enmPropertyType[]; } } = {
    0: {
        title: "مساوی با",
        types: [
            enmPropertyType.boolean,
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    1: {
        title: "نا مساوی با",
        types: [
            enmPropertyType.boolean,
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    2: {
        title: "کوچکتر از",
        types: [
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    3: {
        title: "بزرگتر از",
        types: [
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    4: {
        title: "شروع با", types: [
            enmPropertyType.string
        ]
    },
    5: {
        title: "خاتمه با", types: [
            enmPropertyType.string
        ]
    },
    6: {
        title: "شامل", types: [
            enmPropertyType.string
        ]
    },
    7: {
        title: "کوچکتر یا مساوی با",
        types: [
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    8: {
        title: "بزرگتر یا مساوی با",
        types: [
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    9: {
        title: "شامل نباشد", types: [
            enmPropertyType.string
        ]
    },
    10: {
        title: "فاقد مقدار",
        types: [
            enmPropertyType.boolean,
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
    11: {
        title: "دارای مقدار",
        types: [
            enmPropertyType.boolean,
            enmPropertyType.date,
            enmPropertyType.datetime,
            enmPropertyType.string,
            enmPropertyType.number
        ]
    },
};

export interface IAdvancedSearchColumn {
    title: string;
    name: string;
    controlType?: enmControlType;
    propertyType?: enmPropertyType;
    displayColumn?: string;
    combobox?: {
        options?: { value?: any; title?: string; }[];
        defaultValue?: any;
        mode?: 'multiple' | 'tags';
    };
    lookup?: {
        parent: IBaseComponent;
        lookupComponent: React.ComponentType<any> | string;
        lookupProperty: string;
        lookupDisplayFields: string[];
        preparelookupProps?: (lookupProps: any, form?: FormInstance<any>, isSearchForm?: boolean) => any;
        onLookupOpen?: () => void;
        onLookupClose?: (selectetEntity?: any) => void;
    };
    defaultSearchValue?: string;
}

export interface IAdvancedSearchProps {
    columns: IAdvancedSearchColumn[];
    defaultFilter?: any;
    onSubmit: (args: any, filter: IFilter | null | undefined) => void;
    locked?: boolean;
    defaultSearchParams: IReportDefaultSearchParam[];
}

export interface IAdvancedSearch {
    submit(args: any): void;
    getFilterStructure(): IFilter | null | undefined;
    setDefaultStructure(): void;
}

interface IAdvancedSearchItem {
    getFilter(onlyStructure: boolean, parentLogicalOperator?: 'and' | 'or'): IFilter | null | undefined;
}

export class AdvancedSearch extends Component<IAdvancedSearchProps> implements IAdvancedSearch {
    private batchItem?: Batch | null;
    getFilterStructure(): IFilter | null | undefined {
        return this.batchItem?.getFilter(true);
    }
    submit(args: any): void {
        try {
            let filter = this.batchItem?.getFilter();
            this.props.onSubmit(args, filter);
        }
        catch (e: any) {
            if (e.message === 'validation') {
                Core.warningNotification('لطفا پیام های فیلتر را بررسی نمایید');
            }
        }
    }
    setDefaultStructure(): void {
        this.batchItem?.setStructure();
    }
    render() {
        return (
            <Batch
                itemId={Core.newId()}
                isParent
                columns={this.props.columns}
                ref={node => this.batchItem = node}
                defaultFilter={this.props.defaultFilter}
                locked={this.props.locked}
                defaultSearchParams={this.props.defaultSearchParams}
            />
        );
    }
}

interface IItemProps {
    itemId: string;
    remove?: (id: string) => void;
    columns: IAdvancedSearchColumn[];
    defaultFilter?: any;
    locked?: boolean;
    defaultSearchParams: IReportDefaultSearchParam[];
}

interface IBatchProps extends IItemProps {
    isParent?: boolean;
}

interface IItemStates {
    column?: string;
    controlType?: enmControlType;
    propertyType?: enmPropertyType;
    operator?: enmConditionalOperators;
    value?: any;
    defaultValue?: string;
}

interface IBatchStates {
    logicalOerator: 'and' | 'or';
    not: boolean;
    items: {
        [key: string]: { node: React.ReactNode; ref?: IAdvancedSearchItem | null; }
    };
}

class Batch extends Component<IBatchProps, IBatchStates> implements IAdvancedSearchItem {
    constructor(props: IBatchProps) {
        super(props)
        this.state = {
            not: false,
            logicalOerator: 'and',
            items: {}
        };
    }
    componentDidMount() {
        this.setStructure();
    }
    setStructure() {
        let logicalOpr: "and" | "or" = "and";
        let filter = this.props.defaultFilter?.TypeName === "NotFilter" ? (this.props.defaultFilter as NotFilter).Filter : this.props.defaultFilter;
        if (filter?.TypeName === "OrFilter") {
            logicalOpr = "or";
        }
        this.setState({
            not: this.props.defaultFilter && this.props.defaultFilter?.TypeName === "NotFilter" ? true : false,
            logicalOerator: logicalOpr,
            items: {},
        });
        this.forceUpdate(() => {
            if (filter?.TypeName === "OrFilter" || filter?.TypeName === "AndFilter") {
                this.setFilter(filter);
            }
        });
    }
    setFilter(filter: { Collection: { TypeName: "NotFilter" | "AndFilter" | "OrFilter" | "ComparisonFilter" }[], TypeName: "NotFilter" | "AndFilter" | "OrFilter" }) {
        filter.Collection.forEach(f => {
            if (f.TypeName === "OrFilter" || f.TypeName === "AndFilter" || f.TypeName === "NotFilter") {
                this.addBatch(f as any);
            }
            else if (f.TypeName === "ComparisonFilter") {
                this.addItem(f as any);
            }
        });
    }
    getFilter(onlyStructure: boolean = false): IFilter | null | undefined {
        let filters: IFilter[] = [];
        let filter: IFilter | null = null;
        let isValid = true;
        let hasItem = false;
        for (let key in this.state.items) {
            hasItem = true;
            let search = this.state.items[key].ref;
            let subFilter = search?.getFilter(onlyStructure, this.state.logicalOerator);
            if (subFilter)
                filters.push(subFilter);
            else if (subFilter === null)
                isValid = false;
        }
        if (filters.length) {
            if (this.state.logicalOerator === "and")
                filter = new AndFilter(filters);
            else
                filter = new OrFilter(filters);
            if (this.state.not) {
                filter = new NotFilter(filter);
            }
        }

        if (!hasItem && !this.props.isParent) {
            this.remove();
            return undefined;
        }

        if (!onlyStructure && !isValid)
            throw new Error('validation');

        return filter;
    }
    remove = () => {
        this.props.remove?.call(this, this.props.itemId);
    }
    addItem = (defaultFilter?: { TypeName: "ComparisonFilter" }) => {
        const id = Core.newId();
        let items = this.state.items;
        items[id] = {
            node: <Item
                key={id}
                itemId={id}
                remove={this.removeItem}
                columns={this.props.columns}
                ref={node => {
                    if (items[id])
                        items[id].ref = node;
                }}
                defaultFilter={defaultFilter}
                locked={this.props.locked}
                defaultSearchParams={this.props.defaultSearchParams}
            />
        };
        this.setState({ items });
    }
    addBatch = (defaultFilter?: { TypeName: "AndFilter" | "OrFilter" | "NotFilter" }) => {
        const id = Core.newId();
        let items = this.state.items;
        items[id] = {
            node: <Batch
                key={id}
                itemId={id}
                remove={this.removeItem}
                columns={this.props.columns}
                ref={node => {
                    if (items[id])
                        items[id].ref = node;
                }}
                defaultFilter={defaultFilter}
                locked={this.props.locked}
                defaultSearchParams={this.props.defaultSearchParams}
            />
        };
        this.setState({ items });
    }
    removeItem = (id: string) => {
        let items = this.state.items;
        delete items[id];
        this.setState({ items });
    }
    getItems = () => {
        let items: React.ReactNode[] = [];
        for (let item in this.state.items)
            items.push(this.state.items[item].node);
        return items;
    }
    logicalOperatorChanged = (e: RadioChangeEvent) => {
        this.setState({ logicalOerator: e.target.value });
    }
    notChanged = () => {
        this.setState({ not: !this.state.not });
    }
    render() {
        const extraStyles = this.props.locked ? { display: 'none' } : {};

        return (
            <div style={this.props.isParent ? { padding: 10, background: "rgb(137 167 235 / 20%)" } : { margin: 10, padding: 10, border: "1px solid #dbdbdb", background: "rgb(137 167 235 / 20%)" }}>
                <div style={{ display: "block" }} >
                    <Space style={{ background: "inherit", padding: 2 }}>
                        <Radio.Group disabled={!!this.props.locked} value={this.state.not} buttonStyle="solid" style={{ marginLeft: 10 }} size={"small"}>
                            <Radio.Button style={{ borderRadius: 2 }} onClick={this.notChanged} value={true}>NOT</Radio.Button>
                        </Radio.Group>
                        <Radio.Group disabled={!!this.props.locked} onChange={this.logicalOperatorChanged} value={this.state.logicalOerator}>
                            <Radio value={'and'}>و</Radio>
                            <Radio value={'or'}>یا</Radio>
                        </Radio.Group>
                    </Space>
                    <Space style={{ float: "left", direction: "ltr" }}>
                        {!this.props.isParent && <Tooltip title="حذف گروه"><Button onClick={this.remove} icon={<DeleteOutlined />} /></Tooltip>}
                        <Tooltip title="شرط جدید">
                            <Button style={extraStyles} onClick={() => this.addItem()} icon={<PlusOutlined style={{ verticalAlign: "middle" }} />}>شرط</Button>
                        </Tooltip>
                        <Tooltip title="گروه شرط جدید">
                            <Button style={extraStyles} onClick={() => this.addBatch()} icon={<PlusCircleOutlined style={{ verticalAlign: "middle" }} />}>گروه شرط</Button>
                        </Tooltip>
                    </Space>

                </div>
                <div style={{ display: "block", marginTop: 10, minHeight: 50, border: "1px dashed #dbdbdb", background: "azure", width: "100%" }}>
                    {this.getItems()}
                </div>
            </div>
        );
    }
}

class Item extends Component<IItemProps, IItemStates> implements IAdvancedSearchItem {
    _form?: FormInstance<any> | null;
    constructor(props: IItemProps) {
        super(props)
        if (this.props.defaultFilter?.TypeName === "ComparisonFilter") {
            const comparisonFilter = this.props.defaultFilter;
            const currentColumn = this.props.columns.find(c => c.name === comparisonFilter.Property);
            this.state = {
                column: comparisonFilter.Property,
                value: this.props.defaultSearchParams.find(p => p.Name === comparisonFilter.Value)?.Value ?? comparisonFilter.Value,
                operator: comparisonFilter.Operator.toString() as any,
                controlType: currentColumn?.controlType,
                propertyType: currentColumn?.propertyType,
                defaultValue: comparisonFilter.Value
            };
        }
        else {
            this.state = {};
        }
    }
    getFilter(onlyStructure: boolean = false, parentLogicalOperator?: 'and' | 'or'): IFilter | null | undefined {
        if (!this.state.column || !this.state.operator) {
            if (this._form) {
                this._form.submit();
            }
            return null;
        }
        if (!onlyStructure) {
            let val = this.state.value
            let originalCol = this.props.columns.find(c => c.name === this.state.column);
            if (originalCol?.controlType === enmControlType.lookup) {
                val = this.state.value && this.state.value["Id"];
            }

            if (Number(this.state.operator) === enmConditionalOperators.NullOrEmpty)
                return new IsNullFilter(this.state.column);
            if (Number(this.state.operator) === enmConditionalOperators.NotNullOrEmpty)
                return new NotFilter(new IsNullFilter(this.state.column));
            if (val === undefined || val === null || val === '') {
                if (parentLogicalOperator === 'and')
                    return new TrueFilter();
                else
                    return new FalseFilter();
            }
            return new ComparisonFilter(this.state.column, this.state.operator, val);
        }
        else
            return new ComparisonFilter(this.state.column, this.state.operator, this.state.defaultValue ?? this.state.value);
    }
    remove = () => {
        this.props.remove?.call(this, this.props.itemId);
    }
    onColumnChanged = (colName: string) => {
        const currentColumn = this.props.columns.find(c => c.name === colName);
        let firstOperator = undefined;
        if (currentColumn)
            firstOperator = enmConditionalOperators.Equal.toString() as any;
        this.setState({
            column: colName,
            controlType: currentColumn?.controlType,
            propertyType: currentColumn?.propertyType,
            value: undefined,
            operator: firstOperator,
            defaultValue: undefined
        });
    }
    onOperatorChanged = (value: number) => {
        this.setState({ operator: value as enmConditionalOperators });
    }
    onInputValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ value: e.target.value });
    }
    onDefaultValueChanged = (value: string) => {
        let currentParam = this.props.defaultSearchParams.find(p => p.Name === value);
        let currentValue = currentParam?.Value;
        if (value === "{{CurrentDate}}") {
            if (currentValue && !dayjs.isDayjs(currentValue))
                currentValue = dayjs(currentValue).calendar('jalali');
        }
        this.setState({ defaultValue: value, value: currentValue });
        this._form?.setFields([{ name: this.state.column!, value: currentValue }]);
    }
    onCheckboxValueChanged = (e: CheckboxChangeEvent) => {
        this.setState({ value: e.target.checked });
    }
    onValueChanged = (value: any) => {
        this.setState({ value });
    }
    render() {
        const currentColumn = this.props.columns.find(c => c.name === this.state.column);
        const operatorsList: { name: string; title: string; }[] = [];
        for (let key in operators)
            if (operators[key].types.indexOf(this.state.propertyType ?? enmPropertyType.string) > -1)
                operatorsList.push({ name: key, title: operators[key].title });

        let inputComponent: React.ReactNode;
        let extraParams = {};

        if (currentColumn) {
            switch (this.state.controlType) {
                case enmControlType.combobox:
                    inputComponent = (
                        <Select
                            style={{ width: 'auto', minWidth: 200 }}
                            onChange={this.onValueChanged}
                            key={this.state.column}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                        >
                            {currentColumn?.combobox?.options && currentColumn.combobox.options.map(o => <Option key={o.value} value={o.value}>{o.title}</Option>)}
                        </Select>
                    );
                    extraParams = { initialValue: this.state.value };
                    break;
                case enmControlType.lookup:
                    inputComponent = (
                        <Lookup
                            onChange={this.onValueChanged}
                            isLookupMode={true}
                            property={{
                                title: currentColumn.title, dataIndex: currentColumn.name, allowNull: true, lookup: currentColumn.lookup
                            }}
                            parent={currentColumn.lookup!.parent}
                            key={this.state.column}
                            value={this.state.value}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}

                        />);
                    extraParams = { initialValue: this.state.value };
                    break;
                case enmControlType.date:
                    inputComponent = (
                        <DatePickerJalali
                            key={this.state.column}
                            onChange={(e: any) => {
                                if (e) {
                                    let parsedDate = e.toDate();
                                    parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
                                    this.onValueChanged(parsedDate.toLocaleDateString());
                                }
                                else {
                                    this.onValueChanged(undefined);
                                }
                            }}
                            format="YYYY/MM/DD"
                            style={{ width: '100%', minWidth: 100, display: 'block' }}
                            inputReadOnly={true}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                        />);
                    let currentDate = this.state.value;
                    if (currentDate && !dayjs.isDayjs(currentDate))
                        currentDate = dayjs(currentDate).calendar('jalali');
                    extraParams = { initialValue: currentDate };
                    break;
                case enmControlType.datetime:
                    inputComponent = (
                        <DatePickerJalali
                            key={this.state.column}
                            onChange={(e: any) => {
                                if (e) {
                                    let parsedDate = e.toDate();
                                    parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
                                    this.onValueChanged(parsedDate);
                                }
                                else
                                    this.onValueChanged(undefined);
                            }}
                            format="YYYY/MM/DD HH:mm"
                            style={{ width: '100%', minWidth: 100, display: 'block' }}
                            showTime={true}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                            inputReadOnly={true}
                        />);
                    let currentDatetime = this.state.value;
                    if (currentDatetime && !dayjs.isDayjs(currentDatetime))
                        currentDatetime = dayjs(currentDatetime).calendar('jalali');
                    extraParams = { initialValue: currentDatetime };
                    break;
                case enmControlType.checkbox:
                    inputComponent = (
                        <Checkbox
                            onChange={this.onCheckboxValueChanged}
                            key={this.state.column}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                        />)
                    extraParams = { checked: this.state.value, valuePropName: "checked" };
                    break;
                case enmControlType.numberinput:
                    inputComponent = (
                        <InputNumber
                            onChange={this.onValueChanged}
                            placeholder={currentColumn.title}
                            style={{ width: '100%', display: 'block' }}
                            type={"number"}
                            key={this.state.column}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                        />);
                    extraParams = { initialValue: this.state.value };
                    break;
                case enmControlType.priceinput:
                    inputComponent = (
                        <PriceInput
                            onChange={this.onValueChanged}
                            placeholder={currentColumn.title}
                            style={{ width: '100%' }}
                            key={this.state.column}
                            value={this.state.value}
                            disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                        />);
                    extraParams = { initialValue: this.state.value };
                    break;
                case enmControlType.textbox:
                default:
                    inputComponent = <Input
                        key={this.state.column}
                        onChange={this.onInputValueChanged}
                        disabled={!!this.props.locked && this.state.defaultValue !== undefined}
                    />;
                    extraParams = { initialValue: this.state.value };
                    break;
            }
        }
        const controlType = enmControlType[this.state.controlType as number];
        const lookupComponent = currentColumn?.lookup?.lookupComponent;
        const componentName = lookupComponent ? (lookupComponent instanceof String ? lookupComponent : (lookupComponent as any).name) : undefined;
        const defaultSearchParams = this.props.defaultSearchParams.filter(p => p.Source.split('|')[0] === controlType
            &&
            (
                (p.Source.split('|')[0] === "date")
                ||
                (p.Source.split('|')[0] === "lookup" && p.Source.split('|')[1] === componentName)
            )
        );

        const showValueComponent = enmConditionalOperators.NullOrEmpty !== Number(this.state.operator) && enmConditionalOperators.NotNullOrEmpty !== Number(this.state.operator);

        return (
            <div style={{ padding: 10 }}>
                <Form ref={node => this._form = node}>
                    <Space align="start">
                        {
                            !!this.props.locked
                                ?
                                <></>
                                :
                                <Form.Item style={{ margin: 0, padding: 0 }}>
                                    <Tooltip title="حذف شرط"><Button onClick={this.remove} icon={<DeleteOutlined />} /></Tooltip>
                                </Form.Item>
                        }

                        <Form.Item initialValue={this.state.column} name={this.state.column + "_col"} rules={[{ required: true, message: 'انتخاب ستون اجباری است' }]} style={{ margin: 0, padding: 0 }}>
                            <Select disabled={!!this.props.locked} key={this.state.column + "_col"} style={{ width: 'auto', minWidth: 200 }} onChange={this.onColumnChanged} >
                                {this.props.columns.map(c => <Option key={c.name} value={c.name} >{c.title}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item initialValue={this.state.operator as number} name={this.state.column + "_opr"} rules={[{ required: true, message: 'انتخاب عملگر اجباری است' }]} style={{ margin: 0, padding: 0 }}>
                            <Select disabled={!!this.props.locked} key={this.state.column + "_opr"} style={{ width: 'auto', minWidth: 120, verticalAlign: "top" }} onChange={this.onOperatorChanged}>
                                {operatorsList.map(c => <Option key={c.name} value={c.name} >{c.title}</Option>)}
                            </Select>
                        </Form.Item>
                        {
                            showValueComponent &&
                            <Form.Item name={this.state.column}
                                //rules={[{ required: true, message: 'ورود فیلتر اجباری است' }]}
                                {...extraParams}
                                style={{ margin: 0, padding: 0, minWidth: 300 }}
                            >
                                {inputComponent}
                            </Form.Item>
                        }
                        {
                            inputComponent && !this.props.locked && defaultSearchParams.length
                                ?
                                <Form.Item initialValue={this.state.defaultValue} name={this.state.column + "_defaultvalue"} style={{ margin: 0, padding: 0 }} >
                                    <Select allowClear disabled={!!this.props.locked} key={this.state.column + "_defaultvalue"} style={{ width: 'auto', minWidth: 120, verticalAlign: "top" }} placeholder="مقدار پیشفرض" onChange={this.onDefaultValueChanged} >
                                        {defaultSearchParams.map(c => <Option key={c.Name} value={c.Name} >{c.Title}</Option>)}
                                    </Select>
                                </Form.Item>
                                :
                                <></>
                        }
                    </Space>
                </Form>
            </div>
        );
    }
}